import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import CelebrationIcon from "@mui/icons-material/Celebration";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const RegistrationSuccess = () => {
  const navigate = useNavigate();

  // Confetti particles
  const confettiParticles = Array.from({ length: 120 }, (_, i) => ({
    id: i,
    color: `hsl(${Math.random() * 360}, 100%, 60%)`,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 3,
    size: 6 + Math.random() * 8,
    shape: Math.random() > 0.6 ? "circle" : "rectangle",
    rotation: Math.random() * 360,
  }));

  // Sparkles
  const sparkles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    delay: Math.random() * 5,
    duration: 3 + Math.random() * 4,
    size: 2 + Math.random() * 4,
  }));

  // Floating emojis
  const floatingEmojis = ["🎉", "✨", "🥳", "🎊", "💫", "🌟"];

  // Framer Motion Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8, when: "beforeChildren", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 25, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: { type: "spring", stiffness: 200, damping: 15 },
    },
    hover: { scale: 1.2, rotate: 360, transition: { duration: 1 } },
  };

  const buttonVariants = {
    hidden: { scale: 0 },
    visible: { scale: 1, transition: { type: "spring", stiffness: 300, damping: 20, delay: 1 } },
    hover: { scale: 1.05, boxShadow: "0 10px 25px rgba(33,150,243,0.5)" },
    tap: { scale: 0.95 },
  };

  return (
    <Container
      maxWidth="sm"
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        textAlign: "center",
        background: "transparent",
        overflow: "hidden",
      }}
    >
      {/* Confetti */}
      <AnimatePresence>
        {confettiParticles.map((p) => (
          <motion.div
            key={p.id}
            style={{
              position: "absolute",
              width: p.size,
              height: p.shape === "circle" ? p.size : p.size * 2,
              background: p.color,
              borderRadius: p.shape === "circle" ? "50%" : "3px",
              left: p.left,
              top: "-20px",
              zIndex: 0,
            }}
            animate={{
              y: ["0vh", "100vh"],
              x: [(Math.random() - 0.5) * 50, (Math.random() - 0.5) * 150],
              rotate: p.rotation + 720,
              opacity: [1, 0],
            }}
            transition={{
              duration: p.duration,
              delay: p.delay,
              ease: "easeIn",
              repeat: Infinity,
              repeatDelay: 4,
            }}
          />
        ))}
      </AnimatePresence>

      {/* Sparkles */}
      {sparkles.map((s) => (
        <motion.div
          key={s.id}
          style={{
            position: "absolute",
            width: s.size,
            height: s.size,
            background: "radial-gradient(circle, #fff 0%, #ffeb3b 70%, transparent 100%)",
            borderRadius: "50%",
            left: s.left,
            top: s.top,
            zIndex: 0,
          }}
          animate={{ scale: [0, 1.5, 0], opacity: [0, 1, 0] }}
          transition={{
            duration: s.duration,
            delay: s.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Floating Emojis */}
      {floatingEmojis.map((emoji, i) => (
        <motion.div
          key={i}
          style={{
            position: "absolute",
            fontSize: "1.8rem",
            left: `${10 + Math.random() * 80}%`,
            top: "110%",
            zIndex: 0,
          }}
          animate={{
            y: ["100%", "-120vh"],
            x: [0, (Math.random() - 0.5) * 150],
            rotate: [0, 360],
            opacity: [1, 0.5],
          }}
          transition={{
            duration: 15 + Math.random() * 10,
            delay: Math.random() * 10,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          {emoji}
        </motion.div>
      ))}

      {/* Main Card */}
      <Box
        component={motion.div}
        variants={itemVariants}
        whileHover={{ scale: 1.02 }}
        sx={{
          position: "relative",
          zIndex: 2,
          background: "rgba(255,255,255,0.25)",
          backdropFilter: "blur(16px) saturate(150%)",
          border: "1px solid rgba(255,255,255,0.25)",
          borderRadius: 5,
          p: 5,
          boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
          width: "100%",
        }}
      >
        <motion.div variants={iconVariants} whileHover="hover">
          <CelebrationIcon
            sx={{
              fontSize: 100,
              color: "#FFD54F",
              filter: "drop-shadow(0 0 12px rgba(255,213,79,0.6))",
              mb: 2,
            }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              mb: 2,
              background: "linear-gradient(90deg, #FFD54F, #FF9100, #FF6F00)",
              backgroundSize: "200% 200%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: "gradientShift 4s ease infinite",
            }}
          >
            Thank you so much for registering with us! 
          </Typography >
        </motion.div>

        <motion.div variants={itemVariants}>
          <Typography variant="h6"  sx={{
              fontWeight: "bold",
              mb: 2,
              background: "linear-gradient(90deg, #4fd6ff, #005aff, #5600ff)",
              backgroundSize: "200% 200%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: "gradientShift 4s ease infinite",
            }}>
            We are delighted to have you onboard 🎉
          </Typography>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Typography
            variant="body1"
            sx={{ color: "black", mb: 4, lineHeight: 1.6 }}
          >
            Please check your email to confirm your registration and we are looking forward to work with you 😊
          </Typography>
        </motion.div>

        <motion.div variants={buttonVariants} whileTap="tap">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/login")}
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: "40px",
              textTransform: "none",
              fontWeight: "bold",
              fontSize: "1.1rem",
              background: "linear-gradient(90deg, #2196F3, #21CBF3)",
              boxShadow: "0 8px 25px rgba(33,150,243,0.4)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <motion.span style={{ position: "relative", zIndex: 2 }}>
              Go to Login
            </motion.span>
            <motion.div
              style={{
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)",
              }}
              animate={{ left: "100%" }}
              transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
            />
          </Button>
        </motion.div>
      </Box>

      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </Container>
  );
};

export default RegistrationSuccess;
